import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "../../../form/Button";
import Image from "../../../elements/Image";
import LogoWatermark from "../../../../assets/images/logo-watermark.png";
import { useSelector } from "react-redux";
import { classNames } from "../../../../helpers/classNames";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { eventStatusChange } from "../../../../services/eventService";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { lngDetect } from "../../../../helpers/lngDetect";

const EventInfo = ({ details = {} }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { lang } = useSelector((state) => state?.langProvider);
  const { connect: paymentConnect, loading } = useSelector(
    (state) => state?.paymentDetails?.paymentGetway
  );
  const { selectedOrganizationDetails, organizations } = useSelector(
    (state) => state?.organization
  );
  const location = useLocation();
  const { eventId } = useParams();

  const stepData = [
    {
      _id: 1,
      name: t("Create event"),
      isCurrent: !!location?.pathname?.includes("/user/events/event-manage"),
      ...(eventId &&
        !location?.pathname?.includes("/user/events/event-manage") && {
          link: `/${lang}/user/events/event-manage${
            eventId ? "/" + eventId : ""
          }`,
        }),
    },
    {
      _id: 2,
      name: t("Add Your Ticket"),
      isCurrent:
        !!location?.pathname?.includes("/user/events/tickets") ||
        !!location?.pathname?.includes("/user/events/manage-ticket") ||
        !!location?.pathname?.includes("/user/events/manage-addons"),
      ...(eventId &&
        (!location?.pathname?.includes("/user/events/tickets") ||
          !location?.pathname?.includes("/user/events/manage-ticket")) && {
          link: `/${lang}/user/events/tickets/${eventId}`,
        }),
    },
    {
      _id: 3,
      name: t("Go Live"),
      isCurrent: !!location?.pathname?.includes("/user/events/publish"),
      ...(eventId &&
        !location?.pathname?.includes("/user/events/publish") && {
          link: `/${lang}/user/events/publish/${eventId}`,
        }),
    },
  ];

  const { mutate: publishEventHandler, isPending: publishPending } =
    useMutation({
      mutationFn: async (value) => {
        const res = await eventStatusChange({ ...value });
        return res;
      },
      onSuccess: (res) => {
        if (res?.status === 200) {
          queryClient.setQueryData(["details", eventId], (state) => {
            const state_data = JSON.parse(JSON.stringify(state || {}));
            return {
              ...state_data,
              data: {
                ...state_data?.data,
                published: !state_data?.data?.published,
              },
            };
          });
        } else {
          toast.warning(res?.message);
        }
      },
    });

  return (
    <>
      <div className="bg-white py-6 px-6 rounded-md space-y-6">
        <div className="flex">
          <Button
            buttonIcon={"fa-regular fa-arrow-left"}
            buttonLabel={t("Back to Events")}
            buttonLabelClasses={"text-deep-purple capitalize"}
            buttonClasses={"bg-transparent px-0 h-auto text-deep-purple"}
            buttonHasLink={true}
            buttonLink={`/${lang}/user/events`}
          />
        </div>
        <div className="flex flex-row lg:flex-col gap-5 overflow-auto items-center lg:items-auto">
          <div className="relative bg-secondary-gradient p-0.5 rounded-lg w-full">
            <div className="relative bg-white rounded-md py-4 xl:py-6 px-4 xl:px-6 z-0">
              <div className="w-32 absolute top-6 right-0 z-[-1] opacity-15 left-0 mx-auto">
                <Image
                  src={LogoWatermark}
                  width={"100%"}
                  height={"100%"}
                  alt={""}
                  classes={"object-contain"}
                  effect={"blur"}
                />
              </div>
              <div className="space-y-6">
                <div className="overflow-hidden h-28 lg:h-32 xl:h-40">
                  {lngDetect(details?.data?.title) && (
                    <h2 className="text-lg lg:text-xl xl:text-3xl text-deep-purple font-bold">
                      {lngDetect(details?.data?.title)}
                    </h2>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <Button
                    buttonLabel={t("Preview Event")}
                    buttonClasses={
                      "!bg-none !bg-border-color !text-deep-purple  "
                    }
                    buttonLabelClasses={
                      "text-sm lg:text-lg xl:text-xl !font-semibold capitalize"
                    }
                  />
                  <Button
                    buttonLabel={t("Publish Your Event")}
                    buttonClasses={classNames(
                      "!bg-none !bg-white !border-2 !border-gray-200 !text-deep-purple  ",
                      !eventId
                        ? "opacity-30 !bg-text-gradient-red/30 cursor-not-allow"
                        : ""
                    )}
                    buttonLabelClasses={
                      "text-sm lg:text-lg xl:text-xl !font-semibold capitalize"
                    }
                    isDisable={!details?.data?._id || details?.data?.published}
                    {...(publishPending && {
                      loading: true,
                      buttonIconPosition: "left",
                      isDisable: true,
                    })}
                    buttonFunction={() => {
                      if (paymentConnect && !loading && eventId) {
                        publishEventHandler({
                          ids: [details?.data?._id],
                          published: true,
                        });
                      } else if (!paymentConnect && !loading) {
                        toast.info(
                          "Please setup your payment account first. Please wait!\n We will redirect to payment settings."
                        );
                        setTimeout(() => {
                          navigate(
                            `/${lang}/user/organization/settings/${selectedOrganizationDetails?.data?.uid}?sec=payment`
                          );
                        }, 1000);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="relative bg-secondary-gradient p-0.5 rounded-lg w-full">
            <div className="relative bg-white rounded-md py-4 xl:py-6 px-4 xl:px-6 w-60 md:w-full">
              <div className="space-y-4 md:space-y-6">
                <h2 className="text-lg lg:text-xl xl:text-3xl text-deep-purple font-bold">
                  {t("Steps")}
                </h2>
                {stepData?.length > 0 && (
                  <div className="flex flex-col">
                    {stepData?.map((item, index) => (
                      <div key={index}>
                        {item?.link ? (
                          <Link to={item?.link} key={index}>
                            {index !== 0 && (
                              <div className="flex px-5 py-2">
                                <div className="w-1.5 h-5 bg-text-primary/15 rounded-full"></div>
                              </div>
                            )}
                            <div className="flex items-center gap-3 xl:gap-6">
                              <div className="w-10 xl:w-12 aspect-square flex items-center justify-center rounded-full border border-gradient-red text-deep-purple text-xl font-semibold">
                                {item?.isCurrent ? (
                                  <i className="fa-solid fa-check text-gradient-red text-2xl"></i>
                                ) : (
                                  index + 1
                                )}
                              </div>
                              <div className="text-md xl:text-lg font-semibold text-deep-purple uppercase">
                                {item?.name}
                              </div>
                            </div>
                          </Link>
                        ) : (
                          <div>
                            {index !== 0 && (
                              <div className="flex px-5 py-2">
                                <div className="w-1.5 h-5 bg-text-primary/15 rounded-full"></div>
                              </div>
                            )}
                            <div className="flex items-center gap-3 xl:gap-6">
                              <div className="w-10 md:w-12 aspect-square flex items-center justify-center rounded-full border border-gradient-red text-deep-purple text-xl font-semibold">
                                {item?.isCurrent ? (
                                  <i className="fa-solid fa-check text-gradient-red text-2xl"></i>
                                ) : (
                                  index + 1
                                )}
                              </div>
                              <div className="text-lg font-semibold text-deep-purple uppercase">
                                {item?.name}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventInfo;
